import React from 'react';
// @ts-ignore
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import { useDataEntry } from '../hooks/useDataEntry';
import { KpiDataVersionData } from 'generated/graphql';
import { useSnackbar } from 'hooks/useSnackbar';

export function useDataEntryPage() {
  const history = useHistory();
  const { models, operations } = useDataEntry();
  const { id: companyId } = useParams<{ id: string }>();
  const editButton = React.useRef<any>(null);
  const [columns, setColumns] = React.useState([]);
  const [rows, setRows] = React.useState<any>([]);
  const [cancelConfirmIsOpen, setCancelConfirmIsOpen] = React.useState(false);
  const [publishConfirmIsOpen, setPublishConfirmIsOpen] = React.useState(false);
  const [scrolledOnce, setScrolledOnce] = React.useState({});
  const [editable, setEditable] = React.useState(false);
  const [settingsEditable, setSettingsEditable] = React.useState(false);
  const { addSnackbar } = useSnackbar();
  const companyIdRef = React.useRef();

  const cancelConfirmationMsg = 'Do you want to leave without saving? Any changes made will not be saved.';
  const lockUnlockFailMessage = `Another user is currently in Edit-mode for this Company. Please try again later.`;

  const noTemplate = models.company?.templateId == null;

  const lastPublished = {
    date: models?.company?.lastPublished
      ? moment(models.company.lastPublished)
      : undefined,
    user: models?.company?.lastPublishedBy,
  };

  const settingsChanges = (models.company?.dataCollectionFrequency || [])
    .map((e) => {
      return {
        date: moment(e?.updated),
        user: e?.updatedBy,
        action: 'Settings updated',
      };
    })
    .concat(
      (models.company?.dataCollectionStartDate || []).map((e) => {
        return {
          date: moment(e?.updated),
          user: e?.updatedBy,
          action: 'Settings updated',
        };
      }),
    );

  const lastTopLeftDraft = settingsChanges
    .concat(
      models.company?.lastStatus
        ? {
            date: moment(models.company.lastStatus.date),
            user: models.company.lastStatus.user,
            action: 'Draft Saved',
          }
        : [],
    )
    .sort((a, b) => b.date.valueOf() - a.date.valueOf())[0];

  let activities = settingsChanges
    .concat(
      models.audits
        .filter((e: any) => ['Saved', 'Published'].includes(e.action))
        .map((e: any) => {
          return {
            date: moment(e.changed),
            user: e.changedBy,
            action: {
              Saved: 'Draft saved',
              Published: 'Published',
            }[e.action as 'Saved' | 'Published'],
          };
        }),
    )
    .sort((a, b) => b.date.valueOf() - a.date.valueOf());

  activities = activities.filter(
    (e, i) =>
      !(
        i < activities.length - 1 &&
        e.action === 'Draft saved' &&
        activities[i + 1].action === 'Settings updated'
      ),
  );

  React.useEffect(() => {
    // @ts-ignore
    companyIdRef.current = models.company?.id;
  }, [models.company]);

  React.useEffect(() => {
    // returned function will be called on component unmount
    return () => {
      operations.unLock(companyIdRef.current).then(() => {
        console.log('Local changes called off');
      });
    };
  }, []);

  React.useEffect(() => {
    if (editable) {
      // Listen for Logout event.
      window.addEventListener('logout', handleLogout);
      window.onbeforeunload = () => {
        operations.unLock(companyIdRef.current).then(() => {
          console.log('Local changes called off');
        });
        return 'Are you sure you want to leave?';
      };
    } else {
      window.removeEventListener('logout', handleLogout);
      window.onbeforeunload = null;
    }
    return () => {
      window.removeEventListener('logout', handleLogout);
      window.onbeforeunload = null;
    };
  }, [editable]);

  React.useEffect(() => {
    (async () => {
      if (companyId) {
        await operations.loadCompany(companyId);
      } else {
        const company = await operations.loadDefaultCompany();
        if (company) history.push(`/kpis/data-entry/${company?.id}`);
      }
    })();
  }, [companyId]);

  React.useEffect(() => {
    if (models.rows) {
      setRows(models.rows);
    }
  }, [models.rows]);

  React.useEffect(() => {
    if (
      models.dataVersion &&
      models.dataVersion?.editableByCurrentUser &&
      models.dataVersion?.lockedBy
    ) {
      // todo(vmyshko): stop doing that when we edit Settings
      setEditable(true);
    } else {
      setEditable(false);
    }
  }, [models.dataVersion]);

  React.useEffect(() => {
    if (models.columns) {
      setColumns(
        models.columns.map((c: any) => {
          return {
            field: c,
            headerName: c,
          };
        }),
      );
    }
  }, [models.columns]);

  async function cancelChanges(callbackFn: () => any) {
    const response = await operations.unLock();
    if (response.errorCode === 0) {
      // ok
      callbackFn?.();
    } else {
      // error
      addSnackbar(lockUnlockFailMessage);
    }
  }

  const handleLogout = (e: Event) => {
    e.preventDefault();
    operations.unLock(companyIdRef.current).then(() => {
      console.log('Local changes called off');
      // @ts-ignore
      window.location = '/logout/';
    });
    return false;
  };

  function cancelChangesClick() {
    if (models.hasUnsavedChanges) {
      setCancelConfirmIsOpen(true);
    } else {
      cancelChanges(cancelDataGridChanges).then();
    }
  }

  function cancelDataGridChanges() {
    setEditable(false);
    operations.cancelLocalChanges();

    setRows(models.rows);
  }

  function onGridRowEdit(
    groupName: any,
    { rowId, column, value }: { rowId: string; column: string; value: any },
  ) {
    const gridRows = rows[groupName].map((row: any) => {
      if (row.id === rowId) {
        return { ...row, [column]: value };
      }
      return row;
    });
    const updatedRow = gridRows.find((row: any) => row.id === rowId);

    const changedDataItem = {
      indicatorId: updatedRow.id,
      name: updatedRow.name,
      type: updatedRow.type,
      period: column,
      value: value,
    } as KpiDataVersionData;

    operations.saveLocalChanges(groupName, changedDataItem);

    setRows({
      ...rows,
      [groupName]: gridRows,
    });
  }

  async function publishChanges() {
    const response = await operations.publishData();
    if (response.errorCode === 0) {
      addSnackbar(
        `Published: ${models.company?.name} data was successfully published.`,
      );
      // @ts-ignore
      if (response.payload.kpiDataVersionPublish.lockedBy === null) {
        setEditable(false);
      }
    } else {
      addSnackbar(`Failed to publish`);
    }
  }

  async function startEdit() {
    editButton.current.disabled = true;
    const response = await operations.lock();
    if (response.errorCode === 0 && response.payload?.editableByCurrentUser) {
      setEditable(true);
    } else {
      const lockedUserName = `${response.payload?.lockedBy?.firstName} ${response.payload?.lockedBy?.lastName}`;
      addSnackbar(
        `The user ${lockedUserName} currently in Edit-mode for this Company. Please try again later.`,
      );
    }
    editButton.current.disabled = false;
  }

  return {
    noTemplate,
    lastPublished,
    lastTopLeftDraft,
    cancelConfirmIsOpen,
    setCancelConfirmIsOpen,
    cancelChanges,
    cancelDataGridChanges,
    cancelConfirmationMsg,
    editable,
    editButton,
    startEdit,
    cancelChangesClick,
    companyId,
    addSnackbar,
    publishConfirmIsOpen,
    setPublishConfirmIsOpen,
    publishChanges,
    rows,
    columns,
    onGridRowEdit,
    setScrolledOnce,
    scrolledOnce,
    activities,
    models,
    operations,
    settingsEditable,
    setSettingsEditable,
  };
}
